import React, { useState, useEffect } from 'react';
import { doc, getDoc, setDoc, deleteDoc } from 'firebase/firestore';
import { deleteUser, EmailAuthProvider, reauthenticateWithCredential } from 'firebase/auth';
import { db, auth } from '../firebase';
import { Button, Spinner, Input } from '../components/UI';
import { calculateAge } from '../helpers/utils';

const ModernToggle = ({ label, enabled, onToggle, disabled = false }) => (
    <div 
        onClick={() => !disabled && onToggle()} 
        className={`flex items-center justify-between p-4 rounded-[1.25rem] border transition-all duration-300 ${
            disabled ? 'opacity-50 cursor-not-allowed bg-gray-50 border-gray-100' : 'cursor-pointer bg-white/50 backdrop-blur-md border-white hover:bg-white/80 hover:shadow-md'
        }`}
    >
        <span className="text-gray-800 font-bold text-sm tracking-tight">{label}</span>
        <div className={`relative inline-flex items-center h-7 rounded-full w-14 transition-colors duration-300 shadow-inner ${enabled ? 'bg-emerald-500' : 'bg-gray-200'}`}>
            <span className={`inline-block w-5 h-5 bg-white rounded-full transition-transform duration-300 shadow-sm ${enabled ? 'translate-x-8' : 'translate-x-1'}`} />
        </div>
    </div>
);

const ProfileItem = ({ label, value }) => (
    <div className="bg-white/40 backdrop-blur-sm rounded-[1rem] p-3 border border-white/60">
        <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-1">{label}</p>
        <p className="text-[13px] font-extrabold text-gray-800">{value || '-'}</p>
    </div>
);

export default function ProfileScreen({ user, onLogout, navigate, showToast }) {
    const [settings, setSettings] = useState({ notifications: { immunization: true, assessment: true } });
    const [loading, setLoading] = useState(true);
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
    const [deleteLoading, setDeleteLoading] = useState(false);
    const [deletePassword, setDeletePassword] = useState('');

    useEffect(() => {
        const fetchUserData = async () => {
            if (!user || !user.uid) return;
            setLoading(true);
            try {
                const userDocRef = doc(db, 'users', user.uid);
                const docSnap = await getDoc(userDocRef);
                if (docSnap.exists()) {
                    const data = docSnap.data();
                    setSettings(prev => ({ ...prev, ...data }));
                }
            } catch (error) {
                console.error("Failed to fetch user settings:", error);
            } finally {
                setLoading(false);
            }
        };
        fetchUserData();
    }, [user]);

    const handleToggle = async (key) => {
        const newSettings = { 
            ...settings.notifications, 
            [key]: !settings.notifications[key]
        };
        
        setSettings(prev => ({...prev, notifications: newSettings}));

        try {
            const userDocRef = doc(db, 'users', user.uid);
            await setDoc(userDocRef, { notifications: newSettings }, { merge: true });
        } catch (error) {
            console.error("Error updating notification settings:", error);
        }
    };

    const handleDeleteAccount = async () => {
        if (!deletePassword) {
            if (showToast) showToast('Silakan masukkan password Anda untuk konfirmasi.', 'error');
            return;
        }

        setDeleteLoading(true);
        try {
            // 1. Re-authenticate user to avoid requires-recent-login error
            const credential = EmailAuthProvider.credential(user.email, deletePassword);
            await reauthenticateWithCredential(auth.currentUser, credential);

            // 2. Delete Firestore user document first
            await deleteDoc(doc(db, 'users', user.uid));
            
            // 3. Delete Firebase Auth user
            if (auth.currentUser) {
                await deleteUser(auth.currentUser);
            }
            
            if (showToast) showToast('Akun Anda berhasil dihapus.', 'success');
            onLogout(); // Force return to login screen
        } catch (error) {
            console.error("Error deleting account:", error);
            if (error?.code === 'auth/wrong-password' || error?.code === 'auth/invalid-credential') {
                if (showToast) showToast('Password salah. Gagal menghapus akun.', 'error');
            } else if (error?.code === 'auth/requires-recent-login') {
                if (showToast) showToast('Sesi Anda terlalu lama, silakan Logout dan Login kembali.', 'error');
            } else {
                if (showToast) showToast('Gagal menghapus akun: ' + error.message, 'error');
            }
        } finally {
            setDeleteLoading(false);
        }
    };

    if (loading) {
        return (
            <div className="min-h-screen bg-[#F8FAFC] flex justify-center items-center">
                <div className="bg-white/50 backdrop-blur-xl p-8 rounded-[2rem] shadow-lg border border-white">
                    <Spinner />
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-[#F8FAFC] font-sans selection:bg-purple-200 pb-28 pt-10 px-5 relative">
            {/* Animated Background Mesh */}
            <div className="fixed inset-0 z-0 overflow-hidden pointer-events-none">
                <div className="absolute top-[-10%] left-[-10%] w-[50vw] h-[50vw] bg-purple-200 rounded-full mix-blend-multiply filter blur-[100px] opacity-40 animate-blob"></div>
                <div className="absolute top-[20%] right-[-10%] w-[40vw] h-[40vw] bg-fuchsia-200 rounded-full mix-blend-multiply filter blur-[100px] opacity-30 animate-blob animation-delay-2000"></div>
                <div className="absolute bottom-[-10%] left-[20%] w-[60vw] h-[60vw] bg-pink-200 rounded-full mix-blend-multiply filter blur-[100px] opacity-30 animate-blob animation-delay-4000"></div>
                <div className="absolute inset-0 bg-white/40 backdrop-blur-[50px]"></div>
            </div>

            <div className="relative z-10 max-w-md mx-auto space-y-6">
                
                {/* Profile Header */}
                <div className="text-center mb-10">
                    <div className="relative inline-block mb-4 group">
                        <div className="absolute inset-0 bg-gradient-to-tr from-purple-400 to-pink-400 rounded-[2rem] rotate-6 opacity-50 group-hover:rotate-12 transition-transform duration-300 blur-sm"></div>
                        <div className="relative w-28 h-28 rounded-[2rem] bg-white flex items-center justify-center text-5xl font-extrabold text-purple-600 shadow-lg border-4 border-white transform group-hover:scale-105 transition-transform duration-300">
                            {user.username ? user.username.charAt(0).toUpperCase() : 'U'}
                        </div>
                    </div>
                    <h2 className="text-2xl font-extrabold text-gray-800 tracking-tight">{user.username || 'Pengguna'}</h2>
                    <p className="text-sm text-gray-500 mt-1 font-medium bg-white/50 backdrop-blur-sm inline-block px-3 py-1 rounded-full border border-white">{user.email}</p>
                </div>

                {/* Profile Info Card */}
                <div className="bg-white/60 backdrop-blur-2xl rounded-[1.5rem] p-5 border border-white shadow-[0_8px_30px_rgba(0,0,0,0.04)] relative overflow-hidden">
                    <div className="absolute -right-10 -bottom-10 w-32 h-32 bg-purple-200 rounded-full mix-blend-multiply filter blur-3xl opacity-50 pointer-events-none"></div>
                    
                    <h3 className="text-sm font-extrabold text-gray-800 mb-4 px-1">Informasi Akun</h3>
                    <div className="grid grid-cols-2 gap-3 relative z-10">
                        <ProfileItem label="Peran" value={user.role} />
                        <ProfileItem label="Domisili" value={user.domicile} />
                        {user.parentDob && <ProfileItem label="Usia" value={calculateAge(user.parentDob.toDate())} />}
                    </div>
                    <Button 
                        onClick={() => navigate('editProfile')}
                        className="w-full mt-4 bg-purple-100 text-purple-700 hover:bg-purple-200 font-bold rounded-xl py-3 border border-purple-200 transition-colors shadow-sm"
                    >
                        ✏️ Ubah Profil & Password
                    </Button>
                </div>

                {/* Notification Settings */}
                <div className="bg-white/60 backdrop-blur-2xl rounded-[1.5rem] p-5 border border-white shadow-[0_8px_30px_rgba(0,0,0,0.04)]">
                    <h3 className="text-sm font-extrabold text-gray-800 mb-4 px-1">Pengaturan Notifikasi</h3>
                    <div className="space-y-3">
                        <ModernToggle 
                            label="🔔 Notifikasi Imunisasi" 
                            enabled={settings.notifications?.immunization ?? true} 
                            onToggle={() => handleToggle('immunization')} 
                        />
                        <ModernToggle 
                            label="📅 Notifikasi Pemeriksaan" 
                            enabled={settings.notifications?.assessment ?? true} 
                            onToggle={() => handleToggle('assessment')} 
                        />
                    </div>
                </div>

                {/* About & Logout */}
                <div className="space-y-3 pt-4">
                    <div 
                        onClick={() => navigate('about')}
                        className="group flex items-center justify-between bg-white/70 backdrop-blur-md p-4 rounded-[1.25rem] border border-white cursor-pointer hover:shadow-md transition-all duration-300"
                    >
                        <div className="flex items-center space-x-3">
                            <div className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center text-gray-600 group-hover:bg-sky-100 group-hover:text-sky-600 transition-colors">📱</div>
                            <span className="text-gray-800 font-bold text-sm">Tentang Aplikasi</span>
                        </div>
                        <span className="text-gray-400 group-hover:text-sky-500 group-hover:translate-x-1 transition-all">❯</span>
                    </div>

                    <Button 
                        onClick={onLogout} 
                        className="w-full bg-rose-50 text-rose-600 hover:bg-rose-100 hover:text-rose-700 font-bold rounded-[1.25rem] py-4 border border-rose-200 transition-all duration-300"
                    >
                        Keluar dari Akun
                    </Button>

                    {!user.isVisitor && (
                        <button 
                            onClick={() => setShowDeleteConfirm(true)}
                            className="w-full text-center text-xs font-bold text-red-500 hover:text-red-700 pt-4 pb-2 transition-colors"
                        >
                            ⚠️ Hapus Akun Permanen
                        </button>
                    )}
                </div>

                {/* Delete Confirmation Modal */}
                {showDeleteConfirm && (
                    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-gray-900/60 backdrop-blur-sm">
                        <div className="bg-white rounded-[2rem] p-6 max-w-sm w-full shadow-2xl border border-red-100 animate-fade-in-up">
                            <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center text-3xl mx-auto mb-4">
                                🚨
                            </div>
                            <h3 className="text-xl font-extrabold text-gray-800 text-center mb-2">
                                Hapus Akun Permanen?
                            </h3>
                            <p className="text-sm text-gray-500 text-center mb-4 leading-relaxed">
                                Tindakan ini tidak dapat dibatalkan. Seluruh data profil Anda akan terhapus.
                            </p>
                            <div className="mb-6">
                                <Input 
                                    id="deletePassword" 
                                    type="password" 
                                    label="Masukkan Password Anda" 
                                    value={deletePassword} 
                                    onChange={(e) => setDeletePassword(e.target.value)} 
                                    required 
                                />
                            </div>
                            <div className="space-y-3">
                                <Button 
                                    onClick={handleDeleteAccount} 
                                    disabled={deleteLoading || !deletePassword}
                                    className="w-full bg-red-500 hover:bg-red-600 text-white font-bold py-3 shadow-lg shadow-red-500/30"
                                >
                                    {deleteLoading ? 'Menghapus...' : 'Ya, Hapus Akun Saya'}
                                </Button>
                                <button 
                                    onClick={() => { setShowDeleteConfirm(false); setDeletePassword(''); }}
                                    disabled={deleteLoading}
                                    className="w-full py-3 text-sm font-bold text-gray-600 hover:text-gray-800"
                                >
                                    Batal
                                </button>
                            </div>
                        </div>
                    </div>
                )}
            </div>

            <style>{`
                @keyframes blob {
                    0% { transform: translate(0px, 0px) scale(1); }
                    33% { transform: translate(30px, -50px) scale(1.1); }
                    66% { transform: translate(-20px, 20px) scale(0.9); }
                    100% { transform: translate(0px, 0px) scale(1); }
                }
                .animate-blob { animation: blob 15s infinite alternate ease-in-out; }
                .animation-delay-2000 { animation-delay: 2s; }
                .animation-delay-4000 { animation-delay: 4s; }
            `}</style>
        </div>
    );
}
